import { useState } from 'react';
import { useSelector } from 'react-redux';
import DebtFollowUpService from './DebtFollowUpService';

function useManageDebtFollowUp() {
  const [customerGroups, setCustomerGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tabCounts, setTabCounts] = useState({ tab1: 0, tab2: 0, tab3: 0, tab4: 0, tab5: 0, tab6: 0 });
  const shopName = useSelector((state) => state.shop.name);

  const fetchData = async (step) => {
    setIsLoading(true);
    try {
      const response = await DebtFollowUpService.getDebtFollowUps(shopName, step);
      setCustomerGroups(response.data);
    } catch (err) {
      console.error('Error fetching debt follow-up data:', err);
      setCustomerGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallData = async () => {
    setIsLoading(true);
    try {
      const response = await DebtFollowUpService.getCallFollowUps(shopName);
      setCustomerGroups(response.data);
    } catch (err) {
      console.error('Error fetching call follow-up data:', err);
      setCustomerGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWaitingData = async () => {
    setIsLoading(true);
    try {
      const response = await DebtFollowUpService.getWaitingCallFollowUps(shopName);
      setCustomerGroups(response.data);
    } catch (err) {
      console.error('Error fetching waiting call follow-up data:', err);
      setCustomerGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTabCounts = async () => {
    try {
      const response = await DebtFollowUpService.getTabCounts(shopName);
      setTabCounts(response.data);
    } catch (err) {
      console.error('Error fetching tab counts:', err);
    }
  };

  const markDone = async (customerId, step, adminId, orderIds, trackingNumber = null) => {
    try {
      await DebtFollowUpService.markDone(customerId, step, shopName, adminId, orderIds, trackingNumber);
      return true;
    } catch (err) {
      console.error('Error marking done:', err);
      return false;
    }
  };

  const markCall = async (customerId, adminId) => {
    try {
      await DebtFollowUpService.markCall(customerId, shopName, adminId);
      return true;
    } catch (err) {
      console.error('Error marking call:', err);
      return false;
    }
  };

  const moveToWaiting = async (callFollowUpId, promisedPayDate) => {
    try {
      await DebtFollowUpService.moveToWaiting(callFollowUpId, promisedPayDate);
      return true;
    } catch (err) {
      console.error('Error moving to waiting:', err);
      return false;
    }
  };

  const postponeCall = async (callFollowUpId, promisedPayDate) => {
    try {
      await DebtFollowUpService.postponeCall(callFollowUpId, promisedPayDate);
      return true;
    } catch (err) {
      console.error('Error postponing call:', err);
      return false;
    }
  };

  const fetchLetterData = async (customerId) => {
    try {
      const response = await DebtFollowUpService.getLetterData(customerId, shopName);
      return response.data;
    } catch (err) {
      console.error('Error fetching letter data:', err);
      return null;
    }
  };

  const markPromise = async (customerId, adminId, promisedPayDate, note, currentTab, orderIds) => {
    try {
      await DebtFollowUpService.markPromise(customerId, shopName, adminId, promisedPayDate, note, currentTab, orderIds);
      return true;
    } catch (err) {
      console.error('Error marking promise:', err);
      return false;
    }
  };

  const pushToFinalNotice = async (callFollowUpId, customerId, adminId, targetStep = 2) => {
    try {
      await DebtFollowUpService.pushToFinalNotice(callFollowUpId, customerId, shopName, adminId, targetStep);
      return true;
    } catch (err) {
      console.error('Error pushing to final notice:', err);
      return false;
    }
  };

  const fetchBankInfo = async (amount) => {
    try {
      const response = await DebtFollowUpService.getBankInfo(amount);
      return response.data?.bankInfo || null;
    } catch (err) {
      console.error('Error fetching bank info:', err);
      return null;
    }
  };

  return {
    customerGroups,
    isLoading,
    tabCounts,
    fetchData,
    fetchCallData,
    fetchWaitingData,
    fetchTabCounts,
    markDone,
    markCall,
    markPromise,
    pushToFinalNotice,
    moveToWaiting,
    postponeCall,
    fetchLetterData,
    fetchBankInfo,
  };
}

export default useManageDebtFollowUp;
